// src/student/pages/StudentTestTaking.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Clock,
  AlertTriangle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Flag,
  RotateCcw,
  Send,
} from "lucide-react";
import { studentPortalAPI } from "../services/studentApi";
import { useStudentAuth } from "../context/StudentAuthContext";

export default function StudentTestTaking() {
  const { id: testId } = useParams();
  const navigate = useNavigate();
  const { studentUser } = useStudentAuth();

  const [test, setTest] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({}); // { [questionId]: "A"|"B"|"C"|"D" }
  const [markedForReview, setMarkedForReview] = useState(new Set()); // Set of questionIds
  const [timeLeft, setTimeLeft] = useState(7200); // in seconds
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Fetch test
  useEffect(() => {
    let isMounted = true;
    const fetchTest = async () => {
      try {
        const res = await studentPortalAPI.getTestById(testId);
        if (isMounted && res.success && res.data) {
          setTest(res.data);
          const qList = res.data.questions || [];
          setQuestions(qList);
          const initialSeconds = (res.data.durationMinutes || 120) * 60;
          setTimeLeft(initialSeconds);
        }
      } catch (err) {
        if (isMounted) setError(err.message || "Could not load test questions.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchTest();
    return () => {
      isMounted = false;
    };
  }, [testId]);

  // Countdown Timer
  useEffect(() => {
    if (loading || testResult || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmitTest(true); // auto-submit
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, testResult, timeLeft]);

  // Format time (HH:MM:SS)
  const formattedTime = useMemo(() => {
    const hours = Math.floor(timeLeft / 3600);
    const minutes = Math.floor((timeLeft % 3600) / 60);
    const seconds = timeLeft % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }, [timeLeft]);

  const currentQuestion = questions[currentIndex];

  const handleSelectOption = (optKey) => {
    if (!currentQuestion) return;
    const qId = currentQuestion._id || String(currentIndex);
    setSelectedAnswers((prev) => ({
      ...prev,
      [qId]: optKey,
    }));
  };

  const handleClearChoice = () => {
    if (!currentQuestion) return;
    const qId = currentQuestion._id || String(currentIndex);
    setSelectedAnswers((prev) => {
      const copy = { ...prev };
      delete copy[qId];
      return copy;
    });
  };

  const handleToggleReview = () => {
    if (!currentQuestion) return;
    const qId = currentQuestion._id || String(currentIndex);
    setMarkedForReview((prev) => {
      const next = new Set(prev);
      if (next.has(qId)) {
        next.delete(qId);
      } else {
        next.add(qId);
      }
      return next;
    });
  };

  // Submit Handler
  const handleSubmitTest = async (isAuto = false) => {
    setIsSubmitting(true);
    setShowConfirmModal(false);

    try {
      const payloadAnswers = questions.map((q, idx) => {
        const qId = q._id || String(idx);
        return {
          questionId: q._id,
          selectedAnswer: selectedAnswers[qId] || null,
        };
      });

      const res = await studentPortalAPI.submitTestAttempt(testId, {
        submittedAnswers: payloadAnswers,
        answers: payloadAnswers,
        durationSeconds: ((test?.durationMinutes || 120) * 60) - timeLeft,
        studentEmail: studentUser?.email || "priya.sharma@example.com",
        studentName: studentUser?.name || "Student Aspirant",
      });

      if (res.success && (res.data || res.results)) {
        setTestResult(res.data || res.results);
      }
    } catch (err) {
      alert("Submission error: " + (err.message || "Please try again."));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Summary counts
  const totalCount = questions.length;
  const answeredCount = Object.keys(selectedAnswers).length;
  const reviewCount = markedForReview.size;
  const unansweredCount = totalCount - answeredCount;

  if (loading) {
    return (
      <div className="py-24 text-center space-y-3">
        <div className="w-10 h-10 border-4 border-red-900 border-t-transparent rounded-full animate-spin mx-auto"></div>
        <p className="text-sm font-semibold text-slate-600">Preparing Examination Paper...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-xl mx-auto my-12 p-6 bg-white rounded-2xl border border-red-200 shadow-sm text-center">
        <AlertTriangle className="text-rose-600 mx-auto mb-2" size={32} />
        <h2 className="text-base font-bold text-slate-900 mb-1">Cannot Load Test</h2>
        <p className="text-xs text-slate-600 mb-4">{error}</p>
        <button
          onClick={() => navigate("/student/prelims")}
          className="px-4 py-2 bg-slate-900 text-white rounded-xl text-xs font-semibold"
        >
          Return to Prelims Tests
        </button>
      </div>
    );
  }

  // If test already submitted, render Scorecard View
  if (testResult) {
    return (
      <div className="max-w-3xl mx-auto space-y-6 py-6">
        <div className="bg-white rounded-3xl p-8 border border-slate-200/90 shadow-xl text-center">
          <div className="w-16 h-16 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-4 border border-emerald-100">
            <CheckCircle size={36} />
          </div>

          <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-200">
            Test Submitted Successfully
          </span>

          <h2 className="text-2xl font-black text-slate-900 mt-3 mb-1">
            {test?.title}
          </h2>
          <p className="text-xs text-slate-500">
            Server-side evaluation complete. Marks have been synchronized with your student profile.
          </p>

          {/* Marks Scorecard */}
          <div className="my-8 p-6 bg-gradient-to-r from-red-950 to-[#0b1528] rounded-2xl text-white shadow-lg grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
            <div>
              <span className="block text-[11px] text-slate-300 font-semibold uppercase">Score</span>
              <span className="text-2xl font-black text-amber-300">
                {testResult.marksObtained ?? 0}
              </span>
              <span className="text-[10px] text-slate-400 block">/ {testResult.totalMarks ?? (totalCount * 2)}</span>
            </div>

            <div>
              <span className="block text-[11px] text-slate-300 font-semibold uppercase">Accuracy</span>
              <span className="text-2xl font-black text-emerald-400">
                {testResult.percentage ? `${testResult.percentage}%` : "0%"}
              </span>
              <span className="text-[10px] text-slate-400 block">Overall</span>
            </div>

            <div>
              <span className="block text-[11px] text-slate-300 font-semibold uppercase">Correct</span>
              <span className="text-2xl font-black text-emerald-400">
                {testResult.correctCount ?? 0}
              </span>
              <span className="text-[10px] text-slate-400 block">+2.00 each</span>
            </div>

            <div>
              <span className="block text-[11px] text-slate-300 font-semibold uppercase">Incorrect</span>
              <span className="text-2xl font-black text-rose-400">
                {testResult.incorrectCount ?? 0}
              </span>
              <span className="text-[10px] text-slate-400 block">-0.66 penalty</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={() => navigate("/student/results")}
              className="w-full sm:w-auto px-6 py-2.5 bg-red-900 hover:bg-red-800 text-white rounded-xl text-xs font-bold transition-all shadow-xs"
            >
              View in My Results
            </button>
            <button
              onClick={() => navigate("/student/prelims")}
              className="w-full sm:w-auto px-6 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-semibold transition-colors"
            >
              Browse Other Mock Tests
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Top Test Header Bar */}
      <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-2xs flex flex-wrap items-center justify-between gap-4 sticky top-16 z-30">
        <div>
          <span className="text-[10px] font-bold text-red-800 uppercase tracking-wider bg-red-50 border border-red-100 px-2.5 py-0.5 rounded-full">
            Prelims Examination
          </span>
          <h1 className="text-base font-bold text-slate-900 truncate max-w-md mt-0.5">
            {test?.title}
          </h1>
        </div>

        {/* Live Countdown Timer */}
        <div className="flex items-center gap-4">
          <div
            className={`flex items-center gap-2 px-3.5 py-1.5 rounded-xl border font-mono font-bold text-sm tracking-wide ${
              timeLeft < 300
                ? "bg-rose-50 text-rose-700 border-rose-300 animate-pulse"
                : "bg-slate-50 text-slate-800 border-slate-200"
            }`}
          >
            <Clock size={16} className={timeLeft < 300 ? "text-rose-600" : "text-slate-500"} />
            <span>{formattedTime}</span>
          </div>

          <button
            onClick={() => setShowConfirmModal(true)}
            className="flex items-center gap-1.5 px-4 py-2 bg-red-900 hover:bg-red-800 text-white rounded-xl text-xs font-bold transition-all shadow-xs"
          >
            <Send size={13} />
            <span>Submit Test</span>
          </button>
        </div>
      </div>

      {/* Main Test Layout: Question Area + Question Palette */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
        {/* Left Column: Question Card & Controls */}
        <div className="lg:col-span-3 space-y-4">
          {currentQuestion ? (
            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-2xs space-y-6">
              {/* Question Header */}
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="w-7 h-7 rounded-lg bg-red-900 text-white text-xs font-bold flex items-center justify-center shadow-2xs">
                    {currentIndex + 1}
                  </span>
                  <span className="text-xs font-semibold text-slate-600">
                    Question {currentIndex + 1} of {totalCount}
                  </span>
                </div>

                <div className="flex items-center gap-3 text-xs">
                  <span className="text-emerald-700 font-bold bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100">
                    +2.00
                  </span>
                  <span className="text-rose-700 font-bold bg-rose-50 px-2 py-0.5 rounded border border-rose-100">
                    -0.66
                  </span>
                </div>
              </div>

              {/* Question Text */}
              <div className="text-sm font-medium text-slate-800 leading-relaxed whitespace-pre-line">
                {currentQuestion.questionText || currentQuestion.question}
              </div>

              {/* Options */}
              <div className="space-y-3">
                {["A", "B", "C", "D"].map((optKey) => {
                  const optText = currentQuestion.options
                    ? (Array.isArray(currentQuestion.options)
                        ? currentQuestion.options.find((o) => o.key === optKey)?.text
                        : currentQuestion.options[optKey])
                    : currentQuestion[`option${optKey}`];

                  const qId = currentQuestion._id || String(currentIndex);
                  const isSelected = selectedAnswers[qId] === optKey;

                  return (
                    <button
                      key={optKey}
                      type="button"
                      onClick={() => handleSelectOption(optKey)}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all flex items-start gap-3 ${
                        isSelected
                          ? "border-red-800 bg-red-50/70 shadow-xs"
                          : "border-slate-200 hover:border-slate-300 hover:bg-slate-50/60"
                      }`}
                    >
                      <span
                        className={`w-6 h-6 rounded-lg text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                          isSelected
                            ? "bg-red-900 text-white"
                            : "bg-slate-100 text-slate-600 border border-slate-200"
                        }`}
                      >
                        {optKey}
                      </span>
                      <span
                        className={`text-xs md:text-sm leading-relaxed ${
                          isSelected ? "font-semibold text-slate-900" : "text-slate-700"
                        }`}
                      >
                        {optText || `Option ${optKey}`}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Action Buttons: Mark for Review, Clear Choice, Prev, Next */}
              <div className="pt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleToggleReview}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-colors border ${
                      markedForReview.has(currentQuestion._id || String(currentIndex))
                        ? "bg-purple-50 text-purple-800 border-purple-300"
                        : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"
                    }`}
                  >
                    <Flag size={13} />
                    <span>
                      {markedForReview.has(currentQuestion._id || String(currentIndex))
                        ? "Marked for Review"
                        : "Mark for Review"}
                    </span>
                  </button>

                  {selectedAnswers[currentQuestion._id || String(currentIndex)] && (
                    <button
                      onClick={handleClearChoice}
                      className="flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-semibold text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors"
                    >
                      <RotateCcw size={12} />
                      <span>Clear Response</span>
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    disabled={currentIndex === 0}
                    onClick={() => setCurrentIndex((prev) => prev - 1)}
                    className="flex items-center gap-1 px-3.5 py-2 rounded-xl text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors disabled:opacity-40"
                  >
                    <ChevronLeft size={14} />
                    <span>Previous</span>
                  </button>

                  <button
                    disabled={currentIndex === totalCount - 1}
                    onClick={() => setCurrentIndex((prev) => prev + 1)}
                    className="flex items-center gap-1 px-4 py-2 rounded-xl text-xs font-semibold bg-red-900 text-white hover:bg-red-800 transition-colors shadow-2xs disabled:opacity-40"
                  >
                    <span>Next</span>
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-8 text-center bg-white rounded-2xl border border-slate-200 text-xs text-slate-500">
              No question found at index {currentIndex + 1}
            </div>
          )}
        </div>

        {/* Right Column: Question Palette */}
        <div className="space-y-4">
          <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-2xs space-y-4">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
              Question Palette
            </h3>

            {/* Legend */}
            <div className="grid grid-cols-2 gap-2 text-[10px] text-slate-600 font-medium pb-3 border-b border-slate-100">
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-md bg-emerald-600"></span>
                <span>Answered ({answeredCount})</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-md bg-purple-600"></span>
                <span>Review ({reviewCount})</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-md bg-slate-200"></span>
                <span>Unanswered ({unansweredCount})</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-md border-2 border-red-800"></span>
                <span>Current</span>
              </div>
            </div>

            {/* Palette Grid */}
            <div className="grid grid-cols-5 gap-2 max-h-[320px] overflow-y-auto pr-1">
              {questions.map((q, idx) => {
                const qId = q._id || String(idx);
                const isAnswered = !!selectedAnswers[qId];
                const isReview = markedForReview.has(qId);
                const isCurrent = idx === currentIndex;

                let btnClass = "bg-slate-100 text-slate-700 hover:bg-slate-200";
                if (isAnswered && isReview) {
                  btnClass = "bg-purple-700 text-white font-bold ring-2 ring-emerald-500";
                } else if (isReview) {
                  btnClass = "bg-purple-600 text-white font-bold";
                } else if (isAnswered) {
                  btnClass = "bg-emerald-600 text-white font-bold";
                }

                if (isCurrent) {
                  btnClass += " ring-2 ring-red-900 ring-offset-1";
                }

                return (
                  <button
                    key={qId}
                    onClick={() => setCurrentIndex(idx)}
                    className={`h-8 rounded-lg text-xs font-semibold flex items-center justify-center transition-all ${btnClass}`}
                  >
                    {idx + 1}
                  </button>
                );
              })}
            </div>

            {/* Submit CTA */}
            <button
              onClick={() => setShowConfirmModal(true)}
              className="w-full py-2.5 bg-red-900 hover:bg-red-800 text-white rounded-xl text-xs font-bold transition-all shadow-xs"
            >
              Finish & Submit Test
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-2xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl border border-slate-200 space-y-5">
            <div className="text-center">
              <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-700 flex items-center justify-center mx-auto mb-3 border border-amber-200">
                <AlertTriangle size={24} />
              </div>
              <h3 className="text-base font-bold text-slate-900">Confirm Test Submission</h3>
              <p className="text-xs text-slate-500 mt-1">
                Are you sure you want to finalize your answers? You cannot alter answers after submission.
              </p>
            </div>

            {/* Test Stats Grid */}
            <div className="grid grid-cols-3 gap-2 p-3 bg-slate-50 rounded-xl border border-slate-100 text-center text-xs">
              <div>
                <span className="block text-[10px] text-slate-400 font-bold uppercase">Answered</span>
                <span className="font-bold text-emerald-700 text-sm">{answeredCount}</span>
              </div>
              <div>
                <span className="block text-[10px] text-slate-400 font-bold uppercase">Review</span>
                <span className="font-bold text-purple-700 text-sm">{reviewCount}</span>
              </div>
              <div>
                <span className="block text-[10px] text-slate-400 font-bold uppercase">Unanswered</span>
                <span className="font-bold text-rose-700 text-sm">{unansweredCount}</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-colors"
              >
                Return to Test
              </button>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => handleSubmitTest(false)}
                className="flex-1 py-2.5 bg-red-900 hover:bg-red-800 text-white rounded-xl text-xs font-bold transition-all shadow-xs disabled:opacity-50"
              >
                {isSubmitting ? "Scoring..." : "Yes, Submit Test"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
